import type { SvgIconComponent } from "@mui/icons-material";
import { Box, Button, Dialog, Typography } from "@mui/material";
import { type ReactNode, useId } from "react";

interface CampaignActionDialogProps {
  open: boolean;
  title: string;
  /** Callout icon, shown inline to the LEFT of the body. Omit for a plain subtitle (no callout box). */
  icon?: SvgIconComponent;
  body: ReactNode;
  /** Extra content between the callout and the footer — e.g. the Cancel dialog's money breakdown. */
  extra?: ReactNode;
  primaryLabel: string;
  secondaryLabel: string;
  /** Fill the primary button wine (destructive) instead of rodeo-black. */
  danger?: boolean;
  onPrimary: () => void;
  onClose: () => void;
}

const BTN_BASE = {
  fontFamily: "var(--font-lato)",
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  borderRadius: "6px",
  px: "20px",
  py: "10px",
  minWidth: "unset",
  lineHeight: 1,
} as const;

/**
 * The shared confirm dialog for campaign lifecycle actions, matching the client-experience
 * prototype's `.bc-modal`: a LEFT-aligned Crimson h2, the body inside a tinted wax callout with an
 * inline icon (or a plain subtitle when no icon), and a left-aligned [secondary][primary] button
 * row. Distinct from the app's centered `GfgModal` on purpose — this is the prototype's dialog shape.
 */
export default function CampaignActionDialog({
  open,
  title,
  icon: Icon,
  body,
  extra,
  primaryLabel,
  secondaryLabel,
  danger = false,
  onPrimary,
  onClose,
}: CampaignActionDialogProps) {
  const titleId = useId();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      disableScrollLock
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "12px",
            p: "32px",
            m: 2,
            width: 480,
            maxWidth: "calc(100vw - 32px)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          },
        },
      }}
    >
      <Typography
        id={titleId}
        component="h2"
        sx={{
          fontFamily: "var(--font-crimson)",
          fontSize: "24px",
          fontWeight: 400,
          color: "var(--color-rodeo-black)",
          m: 0,
          mb: "6px",
        }}
      >
        {title}
      </Typography>

      {Icon ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            bgcolor: "var(--color-wax)",
            borderRadius: "10px",
            p: "12px 14px",
            m: "4px 0 8px",
          }}
        >
          <Icon sx={{ fontSize: 20, color: "var(--text-dust-ink)", flexShrink: 0 }} />
          <Box
            sx={{
              fontFamily: "var(--font-lato)",
              fontSize: "13.5px",
              color: "var(--color-rodeo-black)",
              lineHeight: 1.5,
            }}
          >
            {body}
          </Box>
        </Box>
      ) : (
        <Typography
          sx={{
            fontFamily: "var(--font-lato)",
            fontSize: "14px",
            color: "var(--color-graphite)",
            lineHeight: 1.5,
            mb: "20px",
          }}
        >
          {body}
        </Typography>
      )}

      {extra}

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: "8px",
          mt: "24px",
        }}
      >
        <Button
          onClick={onClose}
          disableElevation
          sx={{
            ...BTN_BASE,
            bgcolor: "white",
            color: "var(--color-rodeo-black)",
            border: "1px solid var(--color-smoke)",
            "&:hover": { bgcolor: "white", borderColor: "var(--color-dark-dust)" },
          }}
        >
          {secondaryLabel}
        </Button>
        <Button
          onClick={() => {
            onClose();
            onPrimary();
          }}
          disableElevation
          sx={{
            ...BTN_BASE,
            color: "white",
            bgcolor: danger ? "var(--color-good-wine)" : "var(--color-rodeo-black)",
            "&:hover": { bgcolor: danger ? "var(--color-good-wine-2)" : "#222" },
          }}
        >
          {primaryLabel}
        </Button>
      </Box>
    </Dialog>
  );
}
